import React, { useState, useEffect } from 'react';

const Complete100Analysis = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetch('/complete_100_aspects.json')
      .then(res => res.json())
      .then(data => {
        // Transform data to match UI expectations
        const transformedData = data.map(person => ({
          ...person,
          all_aspects: person.all_100_aspects || person.all_aspects || {},
          summary: {
            ...person.summary,
            marriage_quality: person.summary?.marriage_quality_rating || person.summary?.marriage_quality,
            divorce_risk: person.summary?.divorce_risk_level || person.summary?.divorce_risk,
            divorce_risk_score: person.summary?.divorce_risk_score,
            quality_score: person.summary?.marriage_quality_score || person.summary?.quality_score,
            multiple_marriage_score: person.summary?.multiple_marriage_score,
            multiple_marriage_likely: (person.summary?.multiple_marriage_score || 0) >= 50,
            marriage_promised: person.summary?.marriage_promised !== false,
            marriage_promise_confidence: person.summary?.marriage_promise_confidence || '85%',
            predicted_marriage_age: person.summary?.predicted_marriage_age || person.summary?.marriage_timing,
            predicted_marriage_year: person.summary?.predicted_marriage_year || (person.summary?.birth_year + (person.summary?.predicted_marriage_age || 25))
          }
        }));
        setResults(transformedData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-purple-500 border-t-transparent mx-auto mb-6"></div>
          <p className="text-white text-xl">Loading 100 Aspects Analysis...</p>
          <p className="text-gray-400 mt-2">Processing all historical figures</p>
        </div>
      </div>
    );
  }

  const filteredResults = results.filter(person => 
    person?.summary?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const totalMarriages = results.reduce((sum, r) => sum + (r?.actual_data?.total_marriages || 0), 0);
  const multipleMarriages = results.filter(r => (r?.actual_data?.total_marriages || 0) > 1).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                100 Aspects Analysis
              </h1>
              <p className="text-gray-400 text-sm">Complete Marriage & Relationship Insights</p>
            </div>
            <div className="flex items-center gap-4">
              <input
                type="text"
                placeholder="Search people..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            label="Total People" 
            value={results.length} 
            icon="👥"
            color="from-blue-500 to-cyan-500"
          />
          <StatCard 
            label="Total Marriages" 
            value={totalMarriages} 
            icon="💍"
            color="from-pink-500 to-rose-500"
          />
          <StatCard 
            label="Multiple Marriages" 
            value={multipleMarriages} 
            icon="💔"
            color="from-orange-500 to-amber-500"
          />
          <StatCard 
            label="Aspects Analyzed" 
            value="100" 
            icon="📊"
            color="from-green-500 to-emerald-500"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {selectedPerson ? (
          <PersonDetail 
            person={selectedPerson} 
            onClose={() => setSelectedPerson(null)}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        ) : (
          <PeopleGrid 
            results={filteredResults} 
            onSelect={setSelectedPerson}
          />
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, color }) => (
  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
    <div className="flex items-center gap-3">
      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center text-2xl`}>
        {icon}
      </div>
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className="text-white text-2xl font-bold">{value}</p>
      </div>
    </div>
  </div>
);

const PeopleGrid = ({ results, onSelect }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {results.map((person, index) => {
      const summary = person?.summary || {};
      const actualData = person?.actual_data || {};
      
      return (
        <button
          key={index}
          onClick={() => onSelect(person)}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-left hover:bg-white/10 hover:border-purple-500/50 transition-all group"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white group-hover:text-purple-400 transition-colors">
                {summary.name || 'Unknown'}
              </h3>
              <p className="text-gray-400 text-sm">{summary.birth_date || 'N/A'}</p>
            </div>
            <div className="bg-purple-500/20 px-3 py-1 rounded-full">
              <span className="text-purple-400 font-bold">{actualData.total_marriages || 0}</span>
              <span className="text-gray-500 text-sm"> marriages</span>
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <div className="bg-black/20 rounded-lg p-2">
              <p className="text-gray-500">Lagna</p>
              <p className="text-white font-medium">{summary.lagna || 'N/A'}</p>
            </div>
            <div className="bg-black/20 rounded-lg p-2">
              <p className="text-gray-500">7th Lord</p>
              <p className="text-white font-medium">{summary.seventh_lord || 'N/A'}</p>
            </div>
            <div className="bg-black/20 rounded-lg p-2">
              <p className="text-gray-500">Quality</p>
              <p className={`font-medium ${
                summary.marriage_quality === 'Excellent' ? 'text-green-400' :
                summary.marriage_quality === 'Good' ? 'text-blue-400' :
                summary.marriage_quality === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
              }`}>{summary.marriage_quality || 'N/A'}</p>
            </div>
            <div className="bg-black/20 rounded-lg p-2">
              <p className="text-gray-500">Divorce Risk</p>
              <p className={`font-medium ${
                summary.divorce_risk === 'Low' ? 'text-green-400' :
                summary.divorce_risk === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
              }`}>{summary.divorce_risk || 'N/A'}</p>
            </div>
          </div>
          
          <div className="mt-3 text-purple-400 text-sm flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            View All 100 Aspects →
          </div>
        </button>
      );
    })}
  </div>
);

const PersonDetail = ({ person, onClose, activeTab, setActiveTab }) => {
  const summary = person?.summary || {};
  const aspects = person?.all_aspects || person?.all_100_aspects || {};
  const actualData = person?.actual_data || {};
  
  // Transform domain-based structure to flat structure for UI
  const flatAspects = {
    marriage_promise: aspects?.domain5_marriage?.a41 || {},
    marriage_denial: aspects?.domain5_marriage?.a42 || {},
    marriage_quality: aspects?.domain7_quality || {},
    spouse_personality: aspects?.domain6_spouse?.personality || {},
    spouse_appearance: aspects?.domain6_spouse?.appearance || {},
    timing: aspects?.domain5_marriage?.a44 || {},
    divorce: aspects?.domain8_divorce || {},
    remarriage: aspects?.domain9_remarriage || {},
    children: aspects?.domain10_children || {},
    // Keep all domains for All Aspects tab
    ...aspects
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📋' },
    { id: 'marriage', label: 'Marriage', icon: '💍' },
    { id: 'spouse', label: 'Spouse', icon: '👤' },
    { id: 'timing', label: 'Timing', icon: '⏰' },
    { id: 'divorce', label: 'Divorce Risk', icon: '💔' },
    { id: 'all', label: 'All Aspects', icon: '📊' },
  ];

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white">{summary.name}</h2>
            <p className="text-white/80">Born: {summary.birth_date}</p>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-white transition-colors"
          >
            ← Back to List
          </button>
        </div>
        
        {/* Quick Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          <QuickStat label="Lagna" value={summary.lagna} />
          <QuickStat label="Moon Nakshatra" value={summary.moon_nakshatra} />
          <QuickStat label="7th Lord" value={summary.seventh_lord} />
          <QuickStat label="Darakaraka" value={summary.darakaraka} />
          <QuickStat label="Actual Marriages" value={actualData.total_marriages} highlight />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 px-4 overflow-x-auto">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id 
                  ? 'text-purple-400 border-b-2 border-purple-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && <OverviewTab summary={summary} actualData={actualData} />}
        {activeTab === 'marriage' && <MarriageTab aspects={flatAspects} />}
        {activeTab === 'spouse' && <SpouseTab aspects={flatAspects} />}
        {activeTab === 'timing' && <TimingTab aspects={flatAspects} summary={summary} />}
        {activeTab === 'divorce' && <DivorceTab aspects={flatAspects} />}
        {activeTab === 'all' && <AllAspectsTab aspects={aspects} />}
      </div>
    </div>
  );
};

const QuickStat = ({ label, value, highlight }) => (
  <div className={`${highlight ? 'bg-white/30' : 'bg-white/10'} rounded-lg p-3 text-center`}>
    <p className="text-white/60 text-xs">{label}</p>
    <p className={`${highlight ? 'text-yellow-300' : 'text-white'} font-bold text-lg`}>{value || 'N/A'}</p>
  </div>
);

const OverviewTab = ({ summary, actualData }) => (
  <div className="space-y-6">
    {/* Actual Marriage Data */}
    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
      <h3 className="text-lg font-semibold text-white mb-3">📜 Historical Marriage Record</h3>
      <div className="space-y-2">
        {actualData.marriages?.map((m, i) => (
          <div key={i} className="bg-black/20 rounded-lg p-3 flex items-center justify-between">
            <span className="text-gray-400">Marriage #{i + 1}</span>
            <span className="text-white font-medium">{m.date}</span>
          </div>
        )) || <p className="text-gray-400">No marriage data available</p>}
      </div>
    </div>

    {/* Key Predictions */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h4 className="text-purple-400 font-medium mb-2">Marriage Promise</h4>
        <p className="text-white text-2xl font-bold">
          {summary.marriage_promised ? '✅ Promised' : '⚠️ May be delayed'}
        </p>
        <p className="text-gray-400 text-sm">Confidence: {summary.marriage_promise_confidence}</p>
      </div>
      
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h4 className="text-purple-400 font-medium mb-2">Predicted Marriage Age</h4>
        <p className="text-white text-2xl font-bold">{summary.predicted_marriage_age || 'N/A'} years</p>
        <p className="text-gray-400 text-sm">Year: {summary.predicted_marriage_year}</p>
      </div>
      
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h4 className="text-purple-400 font-medium mb-2">Marriage Quality</h4>
        <p className={`text-2xl font-bold ${
          summary.marriage_quality === 'Excellent' ? 'text-green-400' :
          summary.marriage_quality === 'Good' ? 'text-blue-400' :
          summary.marriage_quality === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
        }`}>{summary.marriage_quality || 'N/A'}</p>
        <p className="text-gray-400 text-sm">Score: {summary.quality_score}%</p>
      </div>
      
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h4 className="text-purple-400 font-medium mb-2">Divorce Risk</h4>
        <p className={`text-2xl font-bold ${
          summary.divorce_risk === 'Low' ? 'text-green-400' :
          summary.divorce_risk === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
        }`}>{summary.divorce_risk || 'N/A'}</p>
        <p className="text-gray-400 text-sm">Score: {summary.divorce_risk_score}%</p>
      </div>
    </div>
    
    {/* Multiple Marriage Indicator */}
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <h4 className="text-purple-400 font-medium mb-2">Multiple Marriage Likelihood</h4>
      <div className="flex items-center gap-4">
        <div className="flex-1 bg-black/30 rounded-full h-4 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-purple-500 to-pink-500 h-full rounded-full transition-all"
            style={{ width: `${summary.multiple_marriage_score || 0}%` }}
          />
        </div>
        <span className="text-white font-bold">{summary.multiple_marriage_score || 0}%</span>
      </div>
      <p className="text-gray-400 text-sm mt-2">
        {summary.multiple_marriage_likely ? '⚠️ Multiple marriages indicated' : '✓ Single marriage most likely'}
      </p>
    </div>
  </div>
);

const MarriageTab = ({ aspects }) => {
  const promise = aspects?.marriage_promise || {};
  const denial = aspects?.marriage_denial || {};
  const quality = aspects?.marriage_quality || {};

  return (
    <div className="space-y-6">
      {/* Marriage Promise Analysis */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Marriage Promise (10-Layer Analysis)</h3>
        <div className="space-y-2">
          {promise.layer_details?.map((layer, i) => (
            <div key={i} className="flex items-center justify-between bg-black/20 rounded-lg p-3">
              <span className="text-gray-300">{layer.layer}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                layer.status === 'Positive' || layer.status === 'Strong' ? 'bg-green-500/20 text-green-400' :
                layer.status === 'Neutral' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {layer.status}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-gray-400">{promise.description}</p>
      </div>

      {/* Marriage Denial Check */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Marriage Denial Factors</h3>
        {denial.denial_factors?.length > 0 ? (
          <div className="space-y-2">
            {denial.denial_factors.map((factor, i) => (
              <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-400">⚠️ {factor}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-green-400">✅ No significant denial factors found</p>
        )}
        <p className="mt-4 text-gray-400">{denial.description}</p>
      </div>

      {/* Marriage Quality */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Marriage Quality Assessment</h3>
        <div className="flex items-center gap-4 mb-4">
          <div className={`text-5xl font-bold ${
            quality.quality_rating === 'Excellent' ? 'text-green-400' :
            quality.quality_rating === 'Good' ? 'text-blue-400' :
            quality.quality_rating === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {quality.quality_score}%
          </div>
          <div>
            <p className="text-white font-medium">{quality.quality_rating}</p>
            <p className="text-gray-400 text-sm">
              Positive: {quality.positive_factors} | Negative: {quality.negative_factors}
            </p>
          </div>
        </div>
        <div className="space-y-1">
          {quality.details?.map((d, i) => (
            <p key={i} className="text-gray-400 text-sm">• {d}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

const SpouseTab = ({ aspects }) => {
  const personality = aspects?.spouse_personality || {};
  const appearance = aspects?.spouse_appearance || {};

  return (
    <div className="space-y-6">
      {/* Spouse Personality */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-4">👤 Spouse Personality</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-black/20 rounded-lg p-3">
            <p className="text-gray-500 text-sm">7th Lord</p>
            <p className="text-white font-medium">{personality.seventh_lord}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3">
            <p className="text-gray-500 text-sm">7th Lord Sign</p>
            <p className="text-white font-medium">{personality.seventh_lord_sign}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3">
            <p className="text-gray-500 text-sm">Darakaraka</p>
            <p className="text-white font-medium">{personality.darakaraka}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3">
            <p className="text-gray-500 text-sm">DK Sign</p>
            <p className="text-white font-medium">{personality.darakaraka_sign}</p>
          </div>
        </div>
        <div className="mt-4 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <p className="text-purple-400 font-medium">Primary Traits:</p>
          <p className="text-white mt-1">{personality.primary_traits}</p>
        </div>
        <div className="mt-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <p className="text-blue-400 font-medium">Sign Influence:</p>
          <p className="text-white mt-1">{personality.sign_influence}</p>
        </div>
      </div>

      {/* Spouse Appearance */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-4">✨ Spouse Physical Appearance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-black/20 rounded-lg p-3 text-center">
            <p className="text-gray-500 text-sm">Height</p>
            <p className="text-white font-medium text-lg">{appearance.height}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3 text-center">
            <p className="text-gray-500 text-sm">Build</p>
            <p className="text-white font-medium text-lg">{appearance.build}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3 text-center">
            <p className="text-gray-500 text-sm">Complexion</p>
            <p className="text-white font-medium text-lg">{appearance.complexion}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3 text-center">
            <p className="text-gray-500 text-sm">7th Sign</p>
            <p className="text-white font-medium text-lg">{appearance.seventh_house_sign}</p>
          </div>
        </div>
        <div className="mt-4 bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
          <p className="text-pink-400 font-medium">Facial Features:</p>
          <p className="text-white mt-1">{appearance.facial_features}</p>
        </div>
      </div>
    </div>
  );
};

const TimingTab = ({ aspects, summary }) => {
  const timing = aspects?.timing || aspects?.marriage_timing || summary || {};
  const multiple = aspects?.domain5_marriage?.a48 || aspects?.multiple_marriages || {};

  return (
    <div className="space-y-6">
      {/* Marriage Windows */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-4">⏰ Marriage Timing Windows</h3>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 text-center">
            <p className="text-gray-400 text-sm">Predicted Age</p>
            <p className="text-white text-3xl font-bold">{timing.predicted_marriage_age}</p>
          </div>
          <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 text-center">
            <p className="text-gray-400 text-sm">Predicted Year</p>
            <p className="text-white text-3xl font-bold">{timing.predicted_marriage_year}</p>
          </div>
          <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 text-center">
            <p className="text-gray-400 text-sm">Moon Nakshatra</p>
            <p className="text-white text-xl font-bold">{timing.moon_nakshatra}</p>
          </div>
        </div>

        <h4 className="text-white font-medium mb-3">Marriage Dasha Windows</h4>
        <div className="space-y-2">
          {timing.marriage_windows?.map((window, i) => (
            <div key={i} className={`rounded-lg p-3 ${
              window.type === 'Primary' ? 'bg-green-500/20 border border-green-500/30' : 'bg-blue-500/20 border border-blue-500/30'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className={`font-medium ${window.type === 'Primary' ? 'text-green-400' : 'text-blue-400'}`}>
                    {window.dasha} Dasha
                  </span>
                  <span className="text-gray-400 text-sm ml-2">({window.type})</span>
                </div>
                <div className="text-right">
                  <p className="text-white font-medium">Ages {window.start_age} - {window.end_age}</p>
                  <p className="text-gray-400 text-sm">{window.start_year} - {window.end_year}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Multiple Marriage Analysis */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-4">💍 Multiple Marriage Analysis</h3>
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 bg-black/30 rounded-full h-6 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full transition-all"
              style={{ width: `${multiple.likelihood_score || 0}%` }}
            />
          </div>
          <span className="text-white font-bold text-xl">{multiple.likelihood_score}%</span>
        </div>
        <p className="text-purple-400 font-medium">{multiple.prediction}</p>
        
        <div className="mt-4 space-y-2">
          {multiple.indicators?.map((ind, i) => (
            <div key={i} className="bg-black/20 rounded-lg p-3">
              <p className="text-gray-300">• {ind}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DivorceTab = ({ aspects }) => {
  const divorce = aspects?.divorce || aspects?.domain8_divorce || aspects?.divorce_indicators || {};

  return (
    <div className="space-y-6">
      {/* Divorce Risk Meter */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">💔 Divorce Risk Assessment</h3>
        
        <div className="flex items-center justify-center mb-6">
          <div className="relative w-48 h-48">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="96" cy="96" r="80" fill="none" stroke="#1f2937" strokeWidth="12" />
              <circle 
                cx="96" cy="96" r="80" fill="none" 
                stroke={divorce.risk_level === 'Low' ? '#22c55e' : divorce.risk_level === 'Moderate' ? '#eab308' : '#ef4444'}
                strokeWidth="12"
                strokeDasharray={`${(divorce.risk_score || 0) * 5.02} 502`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <span className={`text-4xl font-bold ${
                divorce.risk_level === 'Low' ? 'text-green-400' :
                divorce.risk_level === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
              }`}>{divorce.risk_score}%</span>
              <span className="text-gray-400">{divorce.risk_level} Risk</span>
            </div>
          </div>
        </div>

        {/* Divorce Yogas */}
        <h4 className="text-white font-medium mb-3">7 Supreme Divorce Yogas Check</h4>
        {divorce.divorce_yogas?.length > 0 ? (
          <div className="space-y-2">
            {divorce.divorce_yogas.map((yoga, i) => (
              <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-400">⚠️ {yoga}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
            <p className="text-green-400">✅ No major divorce yogas present</p>
          </div>
        )}
        
        <p className="mt-4 text-gray-400">{divorce.description}</p>
      </div>
    </div>
  );
};

const AllAspectsTab = ({ aspects }) => {
  const domains = [
    { key: 'domain1_relationship_foundations', name: 'Domain 1: Relationship Foundations', icon: '💕' },
    { key: 'domain2_psychology', name: 'Domain 2: Relationship Psychology', icon: '🧠' },
    { key: 'domain3_compatibility', name: 'Domain 3: Love Compatibility', icon: '💞' },
    { key: 'domain4_pre_marriage', name: 'Domain 4: Pre-Marriage Destiny', icon: '✨' },
    { key: 'domain5_marriage', name: 'Domain 5: Marriage Promise & Timing', icon: '💍' },
    { key: 'domain6_spouse', name: 'Domain 6: Spouse Profile', icon: '👤' },
    { key: 'domain7_quality', name: 'Domain 7: Marriage Quality', icon: '⭐' },
    { key: 'domain8_divorce', name: 'Domain 8: Divorce Analysis', icon: '💔' },
    { key: 'domain9_remarriage', name: 'Domain 9: Remarriage', icon: '🔄' },
    { key: 'domain10_children', name: 'Domain 10: Children & Legacy', icon: '👶' },
  ];

  return (
    <div className="space-y-4">
      <p className="text-gray-400 mb-4">Complete analysis from all 10 DOMAIN documents (100 aspects):</p>
      
      {domains.map(({ key, name, icon }) => {
        const domainData = aspects?.[key] || {};
        const aspectCount = typeof domainData === 'object' ? Object.keys(domainData).length : 0;
        
        return (
          <details key={key} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden group">
            <summary className="px-4 py-3 cursor-pointer flex items-center justify-between hover:bg-white/5">
              <span className="text-white font-medium">{icon} {name}</span>
              <div className="flex items-center gap-2">
                <span className="text-purple-400 text-sm">{aspectCount} aspects</span>
                <span className="text-gray-500 group-open:rotate-180 transition-transform">▼</span>
              </div>
            </summary>
            <div className="px-4 py-3 bg-black/20 border-t border-white/10">
              {typeof domainData === 'object' && Object.entries(domainData).map(([aspectKey, aspectValue]) => (
                <details key={aspectKey} className="mb-2 bg-black/30 rounded-lg overflow-hidden">
                  <summary className="px-3 py-2 cursor-pointer text-purple-300 hover:bg-white/5">
                    {aspectValue?.name || aspectKey}
                  </summary>
                  <div className="px-3 py-2 border-t border-white/5">
                    <pre className="text-gray-400 text-xs overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(aspectValue, null, 2)}
                    </pre>
                  </div>
                </details>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
};

export default Complete100Analysis;
